import type { SixDetail, ParticipantsGroup, SixFile, SixFolder, EncryptedFileKeyCID, SixActivity } from "@/types/six/Six";
import type { SixActivityDTO, SixFileDTO, SixFolderDTO } from "@/types/six/SixDTOs";
import { normalizeFolderPath } from "@/utils/folderPath.utils";
import { formatDateTime } from "@/utils/format/date.utils";

/**
 * Maps a raw SixDetail API response (with owners, internalParticipants, externalParticipants)
 * into a domain object that includes a structured `participants` object.
 */
export function mapSixSummaryToParticipants(
  meta: SixDetail
): SixDetail & { participants: ParticipantsGroup } {
  const participants: ParticipantsGroup = {
    owners: meta.owners.map((owner) => ({
      id: owner.id,
      name: owner.name,
    })),
    internalParticipants: meta.internalParticipants.map((participant) => ({
      id: participant.id,
      name: participant.name,
    })),
    externalParticipants: meta.externalParticipants.map((participant) => ({
      id: participant.id,
      name: participant.name,
    })),
  };

  return {
    ...meta,
    participants,
  };
}

/**
 * Maps a single SixFileDTO from the API to the domain SixFile type
 */
export function mapSixFileDTOToSixFile(dto: SixFileDTO): SixFile {
  return {
    id: dto.id,
    cid: dto.cid,
    name: dto.name,
    type: dto.type,
    size: dto.size,
    lastModified: formatDateTime(dto.lastModified), // Convert epoch to Date
    path: dto.path,
    encryptedFileKeyCIDS: mapEncryptedFileKeysObjectToArray(dto.encryptedFileKeys),
    downloadedBy: dto.downloadedByUserName,
    uploadedByUserId: dto.uploadedByUserId,
    uploadedByUserName: dto.uploadedByUserName,
  };
}

/**
 * Maps encryptedFileKeys object { userId: cid } to array of EncryptedFileKeyCID
 */
function mapEncryptedFileKeysObjectToArray(
  encryptedFileKeys: Record<string, string> | undefined | null
): EncryptedFileKeyCID[] {
  if (encryptedFileKeys == null || typeof encryptedFileKeys !== 'object') {
    return [];
  }
  return Object.entries(encryptedFileKeys).map(([userId, cid]) => ({
    userId,
    cid,
  }));
}

/**
 * Maps an array of SixFileDTO to SixFile[]
 */
export function mapSixFilesDTOToSixFiles(dtos: SixFileDTO[]): SixFile[] {
  return dtos.map(mapSixFileDTOToSixFile);
}

export function mapSixActivityDTOToSixActivity(dto: SixActivityDTO): SixActivity {
  return {
    id: dto.id,
    action: dto.action,
    userId: dto.userId,
    userName: dto.userName,
    timestamp: formatDateTime(dto.timestamp),
    description: dto.description,
  };
}

/**
 * Maps an array of SixActivityDTO to SixActivity[]
 */
export function mapSixActivitiesDTOToSixActivities(dtos: SixActivityDTO[]): SixActivity[] {
  return dtos.map(mapSixActivityDTOToSixActivity);
}

/**
 * Maps a single SixFolderDTO from the API to the domain SixFolder type.
 */
export function mapSixFolderDTOToSixFolder(dto: SixFolderDTO): SixFolder {
  return {
    id: dto.id,
    path: normalizeFolderPath(dto.path),
    name: dto.name,
    createdAt: formatDateTime(dto.createdAt),
    createdByUserId: dto.createdByUserId,
    createdByUserName: dto.createdByUserName,
  };
}

/**
 * Maps an array of SixFolderDTO to SixFolder[]
 */
export function mapSixFoldersDTOToSixFolders(dtos: SixFolderDTO[]): SixFolder[] {
  return dtos.map(mapSixFolderDTOToSixFolder);
}
